import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import MomentHelpers from "helpers/MomentHelpers";
import { CampaignCount } from "models/interfaces";
import React, { useState } from "react";
import { selectEmail } from "redux/authSlice";
import { downloadOpenedClickReport } from "screens/Campaigns/CampaignService";
import { useAppSelector } from "utils/hooks";

const metricMapping: Record<string, number> = {
  Requested: 1,
  Sent: 2,
  Success: 3,
  Failed: 4,
  Opened: 5,
  Clicked: 6,
  Unsubscribed: 7,
};

interface CustomProps {
  open: boolean;
  setOpen: Function;
  options: string[];
  campaignId?: number;
  counts?: CampaignCount[];
}
const ExportModal = (props: CustomProps) => {
  const theme = useTheme();
  const email = useAppSelector(selectEmail);
  const userTimeZone = MomentHelpers.guessTheTimeZone();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleDownload = async () => {
    if (selectedOptions.length > 0) {
      try {
        setLoading(true);
        const response = await downloadOpenedClickReport(
          props?.campaignId,
          userTimeZone,
          selectedOptions
        );
        openSuccessNotification(response.message);
        setSelectedOptions([]);
        setLoading(false);
        props.setOpen(false);
      } catch (error: any) {
        setLoading(false);
        handleErrorMessage(error);
      }
    }
  };

  const disableCheckBox = (option: string) => {
    if (!props.counts || props.counts.length === 0) {
      return true;
    }
    const enabledOptions = props.options
      .filter((opt) => opt !== "All")
      .filter((opt) => {
        const metricId = metricMapping[opt];
        return metricId
          ? props.counts?.some((c) => c.metricId === metricId && c.count > 0)
          : false;
      });
    if (option === "All") {
      return enabledOptions.length < 2;
    }
    const metricId = metricMapping[option];
    return metricId
      ? !props.counts.some((c) => c.metricId === metricId && c.count > 0)
      : true;
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (name === "All") {
      if (checked) {
        const enabledOptions = props.options
          .filter((opt) => opt !== "All")
          .filter((opt) => {
            const metricId = metricMapping[opt];
            return metricId
              ? props.counts?.some(
                  (c) => c.metricId === metricId && c.count > 0
                )
              : false;
          });

        if (enabledOptions.length >= 2) {
          setSelectedOptions(["All", ...enabledOptions]);
        }
      } else {
        setSelectedOptions([]);
      }
    } else {
      const updatedSelection = checked
        ? [...selectedOptions, name]
        : selectedOptions.filter((opt) => opt !== name);
      if (updatedSelection.length >= 2) {
        setSelectedOptions(["All", ...updatedSelection]);
      } else {
        setSelectedOptions(updatedSelection.filter((opt) => opt !== "All"));
      }
    }
  };

  const getCheckBoxes = () => {
    return props.options.map((option) => {
      return (
        <FormControlLabel
          label={option}
          control={
            <Checkbox
              id="_checkbox"
              name={option}
              checked={selectedOptions.includes(option)}
              onChange={(e: any) => handleActionChange(e)}
              disabled={disableCheckBox(option)}
              sx={{
                "&.Mui-checked": {
                  color: theme.palette.primary.main,
                },
              }}
            />
          }
          sx={{
            width: "100%",
            margin: "0px",
          }}
        />
      );
    });
  };
  const getDialogBody = () => {
    return (
      <>
        <Typography variant="body2" gutterBottom mb={1}>
          The exported file will be emailed to {email}. This may take up to few
          minutes to export the data.
        </Typography>
        <Typography variant="body2" gutterBottom mb={3}>
          {" "}
          Not getting our emails? Contact{" "}
          <a href="mailto:support@mailzzy.com">support@mailzzy.com</a>
        </Typography>
        <Typography variant="h6">Prepare your export file</Typography>
        <Typography gutterBottom variant="body2">
          Select the data you wish to export
        </Typography>
        {getCheckBoxes()}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          mt={3}
        >
          <Button variant="outlined" onClick={() => props.setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedOptions.length === 0}
            onClick={handleDownload}
          >
            Export
          </Button>
        </Stack>
      </>
    );
  };
  return (
    <CustomDialog
      handleDialogClose={() => props.setOpen(false)}
      isDialogOpen={props.open}
      body={getDialogBody()}
      title={
        <Typography color={theme.palette.primary.main} variant="h4">
          Export Campaign Report
        </Typography>
      }
      closable
      closeButtonVisibility
      maxWidth="sm"
    />
  );
};

export default ExportModal;
